/**
 * Collision-aware placement for a `position: fixed` popover panel anchored to
 * a trigger element — the math behind Menu.vue's `placePanel()` (issue 471),
 * pulled out so a second anchored popover (RunningTimer.vue's project picker,
 * issue 637) can share it instead of re-deriving its own alignment rule.
 *
 * The panel opens below the trigger and flips above when the viewport has no
 * room below; it aligns to the trigger's requested edge (`align`) and then
 * slides horizontally to stay fully inside the viewport (minus `edge`) when
 * that alignment would overflow. Staying on screen is the invariant — the
 * requested alignment is only a preference once it cannot be honoured (issue
 * 637: a trigger sitting at the panel's right edge asked for its left-aligned
 * open, and nobody clamped the overflow).
 *
 * Pure — no DOM reads. Every caller supplies the rects itself
 * (`getBoundingClientRect()` results, or the panel's own measured size) and
 * gets back the `top`/`left` to assign, in the same viewport coordinate space
 * the rects came from.
 */

/** The subset of `DOMRect` the placement math needs. */
export interface PopoverAnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PopoverPanelSize {
  width: number;
  height: number;
}

export interface PopoverViewportSize {
  width: number;
  height: number;
}

export interface PopoverPositionOptions {
  /** Which edge of the trigger the panel prefers to align to. Default 'end'. */
  align?: 'start' | 'end';
  /** Gap between the trigger and the panel, in pixels. */
  gap?: number;
  /** Minimum distance the panel keeps from every viewport edge, in pixels. */
  edge?: number;
}

export interface PopoverPosition {
  top: number;
  left: number;
}

const DEFAULT_GAP = 4;
const DEFAULT_EDGE = 8;

export function computePopoverPosition(
  anchor: PopoverAnchorRect,
  panel: PopoverPanelSize,
  viewport: PopoverViewportSize,
  options: PopoverPositionOptions = {},
): PopoverPosition {
  const gap = options.gap ?? DEFAULT_GAP;
  const edge = options.edge ?? DEFAULT_EDGE;
  const align = options.align ?? 'end';

  const below = anchor.bottom + gap;
  const above = anchor.top - gap - panel.height;
  const top = below + panel.height > viewport.height - edge && above >= edge ? above : below;

  const preferred = align === 'start' ? anchor.left : anchor.right - panel.width;
  const maxLeft = Math.max(edge, viewport.width - edge - panel.width);
  const left = Math.min(Math.max(preferred, edge), maxLeft);

  return { top, left };
}
